/***************************************************************************
 * ========================================================================
 * Copyright (c) 2026 Broadcom Inc. and/or its subsidiaries. All Rights Reserved. Broadcom Confidential.
 * ========================================================================
 */

/**
 * @module AviFormsModule
 */

import {
    Component,
    ElementRef,
    forwardRef,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    Optional,
    Renderer2,
    SimpleChanges,
    ViewChild,
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { Subscription } from 'rxjs';
import { FullModalComponentService } from 'ng/shared/components/full-modal/full-modal-component.service';
import './avi-checkbox.component.less';

/**
 * Prefixes for automation testing selector classes.
 */
const CHECKBOX_SELECTOR_CLASS_NAME_PREFIX = 'sel-';
const LABEL_SELECTOR_CLASS_NAME_SELECTOR_PREFIX = 'sel-label-';

/**
 * @description Wrapper around the Clarity checkbox input element.
 * @author alextsg
 */
@Component({
    selector: 'avi-checkbox',
    templateUrl: './avi-checkbox.component.html',
    providers: [
        {
            multi: true,
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => AviCheckboxComponent),
        },
    ],
})
export class AviCheckboxComponent implements ControlValueAccessor, OnInit, OnChanges, OnDestroy {
    /**
     * Name bound to the ngModel.
     */
    @Input()
    public name: string;

    @Input()
    public objectType?: string;

    @Input()
    public fieldName?: string;

    /**
     * @param tooltipText - Custom text unrelated to Schema to be shown.
     */
    @Input()
    public tooltipText?: string;

    /**
     * Set the disabled state of the radio input element.
     */
    @Input()
    public disabled?: boolean;

    /**
     * By default there is a margin-top on this element, which is consistent with Clarity elements.
     * Set noMarginTop to true to remove the margin-top.
     */
    @Input()
    public noMarginTop?: boolean;

    /**
     * String value instead of a boolean that can be set when the checkbox is selected.
     */
    @Input()
    public customTrueValue: boolean | string = true;

    /**
     * String value instead of a boolean that can be set when the checkbox is unselected.
     */
    @Input()
    public customFalseValue: boolean | string = false;

    /**
     * TemplateRef for the input field.
     */
    @ViewChild('inputRef', { static: true })
    public inputRef: ElementRef<HTMLElement>;

    /**
     * Model value passed to the checkbox element.
     */
    public value: boolean;

    /**
     * Class applied to the child input[type=checkbox] element.
     */
    public checkboxClassName: string;

    /**
     * Class applied to the child label element.
     */
    public labelClassName: string;

    /**
     * If false, show the checkbox normally. If true, show the readonly view of the selected option.
     */
    public viewMode = false;

    /**
     * Subscription to the viewMode value.
     */
    private subscription = new Subscription();

    constructor(
        private readonly renderer: Renderer2,
        @Optional()
        fullModalComponentService: FullModalComponentService,
    ) {
        if (fullModalComponentService) {
            this.subscription.add(fullModalComponentService.viewMode$.subscribe(viewMode => {
                this.viewMode = viewMode;
            }));
        }
    }

    /** @override */
    public ngOnInit(): void {
        this.checkboxClassName = `${CHECKBOX_SELECTOR_CLASS_NAME_PREFIX}${this.name}`;
        this.labelClassName = `${LABEL_SELECTOR_CLASS_NAME_SELECTOR_PREFIX}${this.name}`;
    }

    /** @override */
    public ngAfterViewInit(): void {
        this.setDisabled();
    }

    /** @override */
    public ngOnChanges(changes: SimpleChanges): void {
        const { disabled } = changes;

        if (disabled) {
            this.setDisabled(disabled.currentValue);
        }
    }

    /** @override */
    public ngOnDestroy(): void {
        this.subscription.unsubscribe();
    }

    /**
     * Called when the value is changed by the checkbox element.
     */
    public handleModelChange(value: boolean): void {
        this.setValue(value);
    }

    /***********************************************************
     * Implementing ControlValueAccessor Interface
     */

    /**
     * Write the model value.
     */
    public writeValue(value: boolean): void {
        this.value = value;
    }

    /**
     * Set the onChange function.
     */
    public registerOnChange(fn: (value: boolean) => {}): void {
        this.onChange = fn;
    }

    /**
     * Set the onTouched function.
     */
    public registerOnTouched(fn: () => {}): void {
        this.onTouched = fn;
    }

    /***********************************************************/

    /**
     * Set the model value and call onChange.
     */
    private setValue(value: boolean): void {
        this.value = value;

        this.onChange(this.value);
        this.onTouched();
    }

    /**
     * Method to be overriden by the ControlValueAccessor interface.
     */
    private onChange = (value: boolean): void => {};

    /**
     * Method to be overriden by the ControlValueAccessor interface.
     */
    private onTouched = (): void => {};

    /***********************************************************/

    private setDisabled(disabled = this.disabled): void {
        const { nativeElement } = this.inputRef;

        if (disabled || this.viewMode) {
            this.renderer.setAttribute(nativeElement, 'disabled', 'true');
        } else {
            this.renderer.removeAttribute(nativeElement, 'disabled');
        }
    }
}
